// 무한의 탑 51~100층(3개월 C5, 정본: docs/PROGRESSION-720H.md 7절 R6 · docs/CONTENT-90D-PRD.md 5절).
// 1~50층은 content/dailyPlans.ts·daily.ts 그대로이고, 이 파일은 51층 이상의 곡선·테마·이정표·보상 상수만 가진다(순수 데이터, 다른 content 파일을 읽기만 한다).
// 지역5·6 지도·보스는 C2가 붙이는 중이라 판은 지금 있는 지도(r4)와 기존 보스 8종을 쓰고, 몹 레벨(68/80)·HP·피해 곡선만 5·6 구간으로 올린다.
// C2가 r5/r6 지도와 보스를 붙이면 TOWER_BANDS의 mapRegion과 TOWER_HIGH_BOSSES만 바꾸면 된다(곡선·보상은 그대로).
import type { BossKey, WaveDef } from '../levels';
import { PROTECT_SUPPLY, regionGoldMult } from './economy';
import type { RegionNo } from './types';

/** 탑 끝 층(1~50 Day-0 + 51~100 C5) */
export const TOWER_FLOOR_MAX = 100;
/** C5가 여는 첫 층 */
export const TOWER_HIGH_FROM = 51;
/** 1~50층 곡선의 끝(50층): 51층부터 이 값에서 이어 올라간다 */
const TOWER_LOW_MAX = 50;
/** 1~50층 곡선(dailyPlans towerHpMul/towerDmgMul)과 같은 값 — 곡선이 이어지게 하려고 같은 식을 여기서 한 번 더 쓰고, 테스트가 두 쪽이 같은지 본다 */
export const TOWER_LOW_HP_STEP = 0.06;
export const TOWER_LOW_DMG_STEP = 0.04;
const lowHp = (f: number): number => 1 + TOWER_LOW_HP_STEP * (f - 1);
const lowDmg = (f: number): number => 1 + TOWER_LOW_DMG_STEP * (f - 1);

/** 지역 HP 기준(economy가 아니라 world.ts REGION_HP_MULT와 같은 값 — 곡선 양 끝 고정에만 쓴다). 지역4 = 10.2, 지역6 = 45 */
const REGION4_HP = 10.2;
const REGION6_HP = 45;
/**
 * 51~100층 HP 곡선: 50층의 몹 총 HP 배율(지역4 × 50층 계수)에서 100층(지역6 기준 × 50층 계수)까지 층마다 같은 비율로 오른다(기하 곡선, 층당 약 +3.0%).
 * 51층에서 계단이 지지 않고(+3.0%), 100층은 "지역6 1스테이지 몹을 50층 계수만큼 키운 것" — 지역6을 막 연 영웅이 정말 어려워하는 구간이다.
 */
export const TOWER_HIGH_HP_RATIO = Math.pow(REGION6_HP / REGION4_HP, 1 / (TOWER_FLOOR_MAX - TOWER_LOW_MAX));
/** 51층 이상의 HP 계수(50층 = 1). 몹 총 HP = 지역4 배율 × 50층 계수 × 이 값 */
export const towerHighHpFactor = (floor: number): number => Math.pow(TOWER_HIGH_HP_RATIO, Math.max(0, Math.min(TOWER_FLOOR_MAX, floor) - TOWER_LOW_MAX));
/** 51층 이상의 피해 계수: 1~50층 직선(층당 +4%)을 그대로 잇는다(100층 ×4.96). 지역 피해 배율은 지역4 그대로 */
export const towerHighDmgMul = (floor: number): number => lowDmg(Math.min(TOWER_FLOOR_MAX, floor));
/** 50층의 HP 배율(지역4 몹 총 HP를 만드는 계수) */
export const TOWER_HP_AT_50 = lowHp(TOWER_LOW_MAX);

/** 몹 레벨: 51~75층 = 지역5 끝(68), 76~100층 = 지역6 끝(80) (world.ts REGIONS_90D levels와 같은 값, 테스트가 확인) */
export const towerHighMobLevel = (floor: number): number => (floor <= 75 ? 68 : 80);
/** 51~75 → 지역5, 76~100 → 지역6 (보상 골드 배율·권장 구간 이름에 쓴다) */
export const towerHighRegionNo = (floor: number): RegionNo => (floor <= 75 ? 5 : 6);

/** 층 웨이브: 50층(37마리)에서 시작해 100층 48마리까지(MOB_CAPACITY 60 안, 한 웨이브 최대 48) */
export function towerHighWaves(floor: number): WaveDef[] {
  const k = floor - TOWER_LOW_MAX;
  const a = 14 + Math.floor(k / 8), b = 8 + Math.floor(k / 12), c = 6 + Math.floor(k / 15), s = 5 + Math.floor(k / 10);
  return [
    { counts: [a, b, c, s] },
    { counts: [a + 2, b + 1, c + 1, s], elites: 6 + Math.floor(k / 10) },
  ];
}

/** 보스 층(60·70·80·90·100) 보스. 10·30·50층 중간보스급 / 20·40·60… 지역보스급 규칙을 따라 60·80·100 = 지역보스급, 70·90 = 중간보스급 */
export const TOWER_HIGH_BOSSES: Readonly<Record<number, BossKey>> = { 60: 'lich', 70: 'warden', 80: 'ember', 90: 'priest', 100: 'hydra' };

// ── 테마: 10층 구간마다 색·환경 소품 변주·보스 연출 ────────────────────────────────
export interface TowerTheme {
  readonly id: string;
  readonly name: string;
  readonly from: number;
  readonly to: number;
  /** 배경·안개·햇빛 색(0xRRGGBB). render/env/towerMood.ts가 지도 분위기에 곱해 쓴다 */
  readonly bg: number; readonly fog: number; readonly sun: number;
  /** 지도 분위기에 섞는 비율(0 = 지도 그대로, 1 = 이 색). 지도의 지형은 그대로 두고 색만 바꾼다 */
  readonly mix: number;
  /** 환경 소품 변주 키(render가 가진 소품 목록에서 고르는 이름. 없는 키는 무시) */
  readonly props: readonly string[];
  /** 보스 층 연출: 경고 원·등장 문구의 색과 한 줄 */
  readonly bossRim: number;
  readonly bossLine: string;
}
export const TOWER_THEMES: readonly TowerTheme[] = [
  { id: 'sand', name: '모래 회랑', from: 51, to: 60, bg: 0x2a1e12, fog: 0x6b5232, sun: 0xffd9a0, mix: 0.45, props: ['pillar-broken', 'sand-drift'], bossRim: 0xe8c070, bossLine: '모래바람 속에서 서리 리치가 깨어난다' },
  { id: 'mirage', name: '신기루 광장', from: 61, to: 70, bg: 0x2e1a1a, fog: 0x7a4030, sun: 0xff9d6a, mix: 0.5, props: ['obelisk', 'mirage-pool'], bossRim: 0xff8a5a, bossLine: '신기루가 걷히고 묘지기 기사가 길을 막는다' },
  { id: 'rift', name: '심연 균열', from: 71, to: 80, bg: 0x180f2a, fog: 0x3a2860, sun: 0xb58aff, mix: 0.55, props: ['rift-crack', 'floating-shard'], bossRim: 0xb07aff, bossLine: '균열 너머에서 잿불 대주술사가 걸어 나온다' },
  { id: 'chain', name: '사슬 성채', from: 81, to: 90, bg: 0x10101c, fog: 0x2a2a4a, sun: 0x8aa0ff, mix: 0.6, props: ['chain-hang', 'iron-gate'], bossRim: 0x7a8aff, bossLine: '사슬이 끊어지며 독안개 사제가 내려온다' },
  { id: 'throne', name: '허공의 옥좌', from: 91, to: 100, bg: 0x0a0610, fog: 0x2a1030, sun: 0xff5a7a, mix: 0.7, props: ['void-stair', 'throne-ember'], bossRim: 0xff3a5a, bossLine: '옥좌 위의 히드라가 마지막 문을 지킨다' },
];
export function towerThemeOf(floor: number): TowerTheme | null {
  for (const t of TOWER_THEMES) if (floor >= t.from && floor <= t.to) return t;
  return null;
}

// ── 이정표 보상(층을 처음 깰 때 한 번, 층마다 평생 한 번) ──────────────────────────
export interface TowerHighReward { gold: number; stones: number; gems: number; protect: number }
/**
 * 51~100층 첫 클리어 보상. 1~50층 식(골드 200 + 40/층)에 지역 골드 배율 비(지역5/지역4 = 1.85, 지역6/지역4 = 3.4)를 곱해 이어 가고,
 * 5의 배수 층에는 강화석, 보스 층(10의 배수)에는 강화석·젬, 70·90·100층에는 보호 주문서(지역6 클리어 전에는 강화석 7로 대신).
 * 한 번뿐인 보상이라 주간 공급(PROTECT_WEEKLY.tower)을 늘리지 않는다 — 전부 합쳐도 골드 약 44만(층당 평균 약 8.8천 — 지역5·6 스테이지 클리어 골드 3.1천·5.7천의 1.3~2배, 층마다 평생 한 번)·강화석 약 150·보호 3장.
 */
export function towerHighReward(floor: number, region6: boolean): TowerHighReward {
  const r: TowerHighReward = { gold: 0, stones: 0, gems: 0, protect: 0 };
  if (floor < TOWER_HIGH_FROM || floor > TOWER_FLOOR_MAX) return r;
  const m = regionGoldMult(towerHighRegionNo(floor)) / regionGoldMult(4);
  r.gold = Math.round((200 + floor * 40) * m);
  const boss = floor % 10 === 0;
  if (boss) { r.stones = 10 + (floor - 50) / 10 * 4; r.gems = 5 + floor / 10 * 2; }
  else if (floor % 5 === 0) r.stones = 6;
  if (floor === 70 || floor === 90 || floor === 100) { if (region6) r.protect = 1; else r.stones += PROTECT_SUPPLY.preUnlockStones; }
  return r;
}
